import { hasAny, nonEmptyBullets, fmtRange, getSectionMarkerAttrs } from "./utils";
import { contactItems, ContactBit } from "./ContactBits";

// Two-column with optional photo sidebar (left dark, right content).
export default function TwoColumnTemplate({ resume }) {
  const { photo, personal, summary, experience, education, skills, projects, certifications, languages } = resume;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "34% 66%", width: "100%", flex: "1 0 auto", minHeight: "100%", fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10, lineHeight: 1.5, background: "#ffffff", boxSizing: "border-box" }}>
      {/* Sidebar — dark column that must extend to the physical page edge.
          Vertical padding is bumped to 2 cm so text inside the sidebar
          still respects the 2 cm safe area even though the black
          background bleeds to the top/bottom of the paper. Horizontal
          padding stays at 22 px for the intentionally tight sidebar
          columns. */}
      <aside style={{ background: "#0A0A0A", color: "#ffffff", padding: "2cm 22px", boxSizing: "border-box" }}>
        {photo && (
          <div style={{ width: 110, height: 110, borderRadius: "50%", overflow: "hidden", margin: "0 auto 16px", border: "2px solid #ffffff" }}>
            <img src={photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        )}
        <div data-resume-h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: 20, textAlign: "center", lineHeight: 1.1 }}>{personal.fullName || "Your Name"}</div>
        {personal.title && <div style={{ textAlign: "center", fontSize: 10, color: "#a3a3a3", marginTop: 4 }}>{personal.title}</div>}

        <div data-resume-contact="true">
          <SideLabel>Contact</SideLabel>
          {contactItems(personal, { includeLinks: false }).map((it) => (
            <div key={it.key} style={{ marginBottom: 2, wordBreak: "break-all" }}>
              <ContactBit Icon={it.Icon} value={it.value} size={10} color="#ffffff" />
            </div>
          ))}

          {contactItems(personal, { includeContact: false }).length > 0 && (
            <>
              <SideLabel>Links</SideLabel>
              {contactItems(personal, { includeContact: false }).map((it) => (
                <div key={it.key} style={{ marginBottom: 2, wordBreak: "break-all" }}>
                  <ContactBit Icon={it.Icon} value={it.value} size={10} color="#ffffff" />
                </div>
              ))}
            </>
          )}
        </div>

        {skills?.length > 0 && (
          <div data-resume-skills="true">
            <SideLabel>Skills</SideLabel>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {skills.map((s, i) => <span key={`${s}-${i}`} style={{ border: "1px solid rgba(255,255,255,0.25)", padding: "2px 6px", fontSize: 9 }}>{s}</span>)}
            </div>
          </div>
        )}

        {hasAny(education, ["school", "degree"]) && (
          <div data-resume-education="true">
            <SideLabel>Education</SideLabel>
            {education.map((ed, i) => (ed.school || ed.degree) && (
              <div key={ed.id || i} style={{ marginBottom: 6 }}>
                <div style={{ fontWeight: 600 }}>{ed.school}</div>
                <div style={{ color: "#a3a3a3", fontSize: 9.5 }}>{ed.degree}{ed.field ? `, ${ed.field}` : ""}</div>
                <div style={{ color: "#737373", fontSize: 9 }}>{fmtRange(ed.startDate, ed.endDate)}</div>
              </div>
            ))}
          </div>
        )}

        {hasAny(certifications, ["name"]) && (
          <div data-resume-certifications="true">
            <SideLabel>Certifications</SideLabel>
            {certifications.map((c, i) => c.name && (
              <div key={c.id || i} style={{ marginBottom: 4 }}>
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <div style={{ color: "#a3a3a3" }}>{c.issuer}{c.date ? ` · ${c.date}` : ""}</div>
              </div>
            ))}
          </div>
        )}

        {languages?.some((l) => l.name?.trim()) && (
          <div data-resume-additional="true">
            <SideLabel>Languages</SideLabel>
            {languages.map((l) => l.name?.trim() && (
              <div key={l.id} style={{ marginBottom: 3 }}>
                <span style={{ fontWeight: 600 }}>{l.name}</span>
                {l.level && <span style={{ color: "#a3a3a3" }}> · {l.level}</span>}
              </div>
            ))}
          </div>
        )}
      </aside>

      {/* Main — text-only column. Uses 2 cm vertical and right padding
          to honour the page-margin contract; the left inset stays at
          32 px so the main content keeps a comfortable gap from the
          full-bleed dark sidebar (which supplies the perceived left
          margin visually). */}
      <main style={{ padding: "2cm 2cm 2cm 32px", color: "#0a0a0a", boxSizing: "border-box" }}>
        {summary && <MainSec title="Professional Summary"><div>{summary}</div></MainSec>}

        {hasAny(experience, ["company", "role"]) && (
          <MainSec title="Experience">
            {experience.map((e, i) => {
              const bullets = nonEmptyBullets(e.bullets);
              if (!e.company && !e.role && bullets.length === 0) return null;
              return (
                <div key={e.id || i} data-page-item="true" style={{ marginBottom: 12 }}>
                  <div data-resume-jobtitle style={{ fontWeight: 700, fontSize: 11 }}>{e.role}</div>
                  <div style={{ color: "#525252", fontSize: 10 }}><span data-resume-company>{e.company}</span>{e.location ? <> · <span data-resume-location>{e.location}</span></> : ""} <span style={{ color: "#a3a3a3" }}>· <span data-resume-date>{fmtRange(e.startDate, e.endDate)}</span></span></div>
                  {bullets.length > 0 && (
                    <ul data-resume-workdesc style={{ margin: "3px 0 0 14px", padding: 0 }}>
                      {bullets.map((b, bi) => <li key={`${e.id || i}-b-${bi}`} style={{ marginBottom: 2 }}>{b}</li>)}
                    </ul>
                  )}
                </div>
              );
            })}
          </MainSec>
        )}

        {hasAny(projects, ["name"]) && (
          <MainSec title="Projects">
            {projects.map((p, i) => p.name && (
              <div key={p.id || i} style={{ marginBottom: 6 }}>
                <div><span data-resume-projectname style={{ fontWeight: 700 }}>{p.name}</span>{p.link ? <span style={{ color: "#2400FF", fontWeight: 400 }}> · {p.link}</span> : null}</div>
                {p.tech && <div style={{ color: "#737373", fontSize: 9.5 }}>{p.tech}</div>}
                {p.description && <div>{p.description}</div>}
              </div>
            ))}
          </MainSec>
        )}
      </main>
    </div>
  );
}

function SideLabel({ children }) {
  return <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8.5, letterSpacing: "0.22em", textTransform: "uppercase", color: "#a3a3a3", margin: "14px 0 4px" }}>{children}</div>;
}

function MainSec({ title, children }) {
  return (
    <section {...getSectionMarkerAttrs(title)} style={{ marginBottom: 16 }}>
      <div data-resume-h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: 14, textTransform: "uppercase", letterSpacing: "-0.01em", marginBottom: 6, borderBottom: "2px solid #0a0a0a", paddingBottom: 3 }}>{title}</div>
      {children}
    </section>
  );
}
